import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, Requirement } from '../contexts/ApiContext';
import Button from '../components/ui/Button';

const ListCompanyRequirementsPage: React.FC = () => {
    const navigate = useNavigate();
    const { allRequirements, loadingRequirements, fetchRequirements } = useApi();

    useEffect(() => {
        fetchRequirements();
    }, [fetchRequirements]);

    const handleGoBack = () => {
        navigate('/company-menu');
    };

    const handleRequirementClick = (requirement: Requirement) => {
        // Prevent navigation if requirement is approved
        if (requirement.status === 'approved') {
            return;
        }
        navigate('/requirement-detail', { state: { requirement } });
    };

    if (loadingRequirements) {
        return (
            <div className="container">
                <div className="header">
                    <h1>📄 Company Requirements</h1>
                    <p>Loading company requirements...</p>
                </div>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                    <p>Loading requirements...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="header">
                <h1>📄 Company Requirements</h1>
                <p>View and manage your company requirements</p>
            </div>

            <div className="form-container">
                <div className="requirements-list-container">
                    {allRequirements && allRequirements.companyRequirements && allRequirements.companyRequirements.length > 0 ? (
                        <div className="requirement-buttons-list">
                            {allRequirements.companyRequirements.map((requirement) => {
                                const isRejected = requirement.status === 'rejected';
                                const isExpired = requirement.status === 'expired';
                                const isApproved = requirement.status === 'approved';
                                const isPendingReview = requirement.signed && !isRejected && !isExpired && !isApproved;

                                return (
                                    <button
                                        key={requirement.requirement_id}
                                        className={`requirement-button ${
                                            isRejected ? 'rejected' :
                                            isExpired ? 'expired' :
                                            isApproved ? 'approved' :
                                            isPendingReview ? 'pending-review' :
                                            'unsigned'
                                        }`}
                                        onClick={() => handleRequirementClick(requirement)}
                                        disabled={isApproved}
                                        style={isApproved ? { cursor: 'not-allowed', opacity: 0.7 } : {}}
                                    >
                                        <div className="requirement-button-content">
                                            <span className="requirement-icon">
                                                {isRejected ? '❌' :
                                                 isExpired ? '⏱️' :
                                                 isApproved ? '✅' :
                                                 isPendingReview ? '🕐' :
                                                 '📝'}
                                            </span>
                                            <span className="requirement-name">
                                                {requirement.requirement_name}
                                            </span>
                                            {isRejected && (
                                                <span className="rejected-badge">Rejected</span>
                                            )}
                                            {isExpired && (
                                                <span className="expired-badge">Expired</span>
                                            )}
                                            {isApproved && (
                                                <span className="approved-badge">Approved</span>
                                            )}
                                            {isPendingReview && (
                                                <span className="pending-review-badge">Pending Review</span>
                                            )}
                                            {!isRejected && !isExpired && !isApproved && !isPendingReview && (
                                                <span className="todo-badge">To Do</span>
                                            )}
                                        </div>
                                        {requirement.description && (
                                            <div className="requirement-description">
                                                {requirement.description}
                                            </div>
                                        )}
                                        {requirement.expiry_date_indexed && (
                                            <div className="requirement-expiry-date">
                                                <strong>Expiry Date:</strong> {new Date(requirement.expiry_date_indexed).toLocaleDateString('en-GB')}
                                            </div>
                                        )}
                                        {isRejected && requirement.status_comment && (
                                            <div className="rejection-reason" style={{
                                                marginTop: '8px',
                                                padding: '8px 12px',
                                                backgroundColor: '#ffebee',
                                                borderLeft: '3px solid #f44336',
                                                borderRadius: '4px',
                                                fontSize: '13px',
                                                color: '#c62828'
                                            }}>
                                                <strong>Rejection Reason:</strong> {requirement.status_comment}
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="no-requirements">
                            <p>📭 No company requirements available at this time.</p>
                        </div>
                    )}

                    <div className="action-buttons" style={{ marginTop: '30px' }}>
                        <Button variant="secondary" onClick={handleGoBack}>
                            ← Go Back
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ListCompanyRequirementsPage;

